<script setup lang="ts">
    import type { CountryListItem } from '~/utils/countryConfig';

    const props = defineProps<{
        country: CountryListItem;
        pendingCountryCode: string;
        pendingLanguageCode: string;
        isGlobal?: boolean;
        dropdown?: boolean;
    }>();

    const emit = defineEmits<{
        (e: 'select-country-default-language', countryCode: string): void;
        (e: 'select-country-language', countryCode: string, languageCode: string): void;
    }>();

    const { t } = useI18n();

    const isActiveCountry = computed(() => props.pendingCountryCode === props.country.key);

    const selectedDropdownLanguage = computed(() =>
        isActiveCountry.value ? props.pendingLanguageCode : props.country.languages[0]
    );

    const onSelectCountryDefaultLanguage = () => {
        emit('select-country-default-language', props.country.key);
    };

    const onSelectCountryLanguage = (language: string) => {
        emit('select-country-language', props.country.key, language);
    };

    const languageOptions = computed(() =>
        props.country.languages
            .map((lang) => ({
                label: getNativeLanguageName(lang, t),
                value: lang
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
    );

    const onDropdownChange = (value: string | number) => {
        emit('select-country-language', props.country.key, String(value));
    };
</script>

<template>
    <div
        class="country-item"
        :class="{ '--active': isActiveCountry, '--global': isGlobal }"
        role="radio"
        :aria-checked="isActiveCountry"
        :tabindex="isActiveCountry ? 0 : -1"
        @click="onSelectCountryDefaultLanguage"
        @keydown.enter.self.prevent="onSelectCountryDefaultLanguage"
        @keydown.space.self.prevent="onSelectCountryDefaultLanguage"
    >
        <div class="country-header">
            <div class="country-info">
                <img
                    v-if="country.flagUrl"
                    class="country-flag"
                    :src="country.flagUrl"
                    :alt="country.flagAlt"
                />

                <GlobeIcon
                    v-else-if="isGlobal"
                    aria-hidden="true"
                    class="country-flag"
                />

                <span class="country-name">{{ country.name }}</span>
            </div>

            <span
                class="country-radio"
                aria-hidden="true"
            ></span>
        </div>

        <div
            v-if="dropdown"
            class="language-dropdown-wrapper"
            @click.stop
        >
            <BaseSelect
                id="header-language-dropdown"
                class="language-dropdown"
                :options="languageOptions"
                :model-value="selectedDropdownLanguage"
                @update:model-value="onDropdownChange"
            />
        </div>

        <div
            v-else
            class="country-languages"
            @click.stop
        >
            <button
                v-for="language in country.languages"
                :key="`${country.key}-${language}`"
                class="language-link"
                :class="{
                    '--active': isActiveCountry && pendingLanguageCode === language
                }"
                @click="onSelectCountryLanguage(language)"
                @keydown.enter.stop.prevent="onSelectCountryLanguage(language)"
                @keydown.space.stop.prevent="onSelectCountryLanguage(language)"
            >
                <span class="language-name">
                    {{ getNativeLanguageName(language, $t) }}
                </span>
            </button>
        </div>
    </div>
</template>

<style scoped lang="scss">
    @import '~/assets/scss/navigation/header-language-picker-country-item.scss';

    .language-dropdown {
        margin-left: calc(var(--spacing--05) * -1);
    }
</style>
